import {DataStatus, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

// eslint-disable-next-line no-restricted-imports
import {defaultTo, isNil, mergeAll, path} from 'ramda';
import {isArray, isEmptyString} from 'ramda-adjunct';

import {
  useCompleteFastTrackItemMutation,
  useDeleteFastTrackBasketItemMutation,
  useGetFastTrackIssueQuery,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  handleApiError,
  logError,
  refreshDmsDatagrid,
  useTenant,
} from '@dms/shared';
import {OverviewTotalPrice} from '@dms/warehouse/shared-components';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

interface ListOfItemsTabProps extends RequiredTestIdProps {
  fastTrackIssueId: string;
}

const PREDEFINED_COLUMNS = ['warehouse', 'fastTrackItemId', 'articleId'] as const;

export function ListOfItemsTab(props: ListOfItemsTabProps) {
  const navigate = useNavigate();

  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();
  const fastTrackIssueQuery = useGetFastTrackIssueQuery(
    {fastTrackId: props.fastTrackIssueId},
    {skip: isNil(props.fastTrackIssueId) || isEmptyString(props.fastTrackIssueId)}
  );

  const [deleteFastTrackBasketItem] = useDeleteFastTrackBasketItemMutation();
  const [completeFastTrackItem] = useCompleteFastTrackItemMutation();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {fastTrackId: props.fastTrackIssueId}]),
    [props.fastTrackIssueId]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowData,
    getColumnData,
  }) => {
    const warehouseId = getColumnData<{key: string}>('warehouse')?.value.key;
    const articleId = getColumnData<string>('articleId')?.value;
    const fastTrackItemId = isArray(rowData)
      ? (rowData.map((row) => path(['fastTrackItemId', 'value'], row)) as string[])
      : ([path(['fastTrackItemId', 'value'], rowData)] as string[]);

    if (isNil(warehouseId)) {
      return logError('WarehouseId is missing');
    }

    if (isNil(articleId)) {
      return logError('ArticleId is missing');
    }

    if (isNil(fastTrackItemId)) {
      return logError('Fast track item id is missing');
    }

    match(actionKey)
      .with('redirectDetail', () => {
        navigate(
          composePath(warehouseRoutes.articleDetailOverview, {
            params: {
              warehouseId,
              id: articleId,
            },
            isSearchParamsPreserved: false,
          })
        );
      })
      .with('delete', () => {
        deleteFastTrackBasketItem({
          fastTrackId: props.fastTrackIssueId,
          body: {
            fastTrackItemIds: fastTrackItemId,
          },
        })
          .unwrap()
          .then(() => showNotification.success(i18n.t('entity.warehouse.notifications.deleted')))
          .then(() => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.fastTrackIssueItems))
          .catch(handleApiError);
      })
      .with('complete', () => {
        completeFastTrackItem({
          fastTrackId: props.fastTrackIssueId,
          body: {
            fastTrackItemIds: fastTrackItemId,
          },
        })
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.actions.update')))
          .then(() => refreshDmsDatagrid(WAREHOUSE_GRID_CODES.fastTrackIssueItems))
          .catch(handleApiError);
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const totalPriceWithoutVat = formatCurrency(
    defaultTo(0, fastTrackIssueQuery.data?.totalPrice?.withoutVat.amount),
    defaultTo(tenant.tenantCurrency, fastTrackIssueQuery.data?.totalPrice?.withoutVat?.currency),
    2
  );

  const totalPriceWithVat = formatCurrency(
    defaultTo(0, fastTrackIssueQuery.data?.totalPrice?.withVat.amount),
    defaultTo(tenant.tenantCurrency, fastTrackIssueQuery.data?.totalPrice?.withVat?.currency),
    2
  );

  return (
    <DataStatus
      isLoading={fastTrackIssueQuery.isLoading}
      isError={fastTrackIssueQuery.isError}
      minHeight="100%"
    >
      <Box height="95%" data-testid={suffixTestId('wrapper', props)}>
        <DmsDataGrid
          gridCode={WAREHOUSE_GRID_CODES.fastTrackIssueItems}
          actionCallback={actionCallback}
          queryModifier={queryModifier}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={WAREHOUSE_GRID_CODES.fastTrackIssueItems}
        />
      </Box>
      <OverviewTotalPrice
        totalPriceWithoutVat={totalPriceWithoutVat}
        totalPriceWithVat={totalPriceWithVat}
        data-testid={suffixTestId('totalPrice', props)}
      />
    </DataStatus>
  );
}
